// VEC-3436: validación de campos obligatorios de un ítem/tarea del presupuesto detallado.
//
// costo y precio son alias del mismo monto: el ítem guarda el valor en 'costo' y la tarea en 'precio',
// pero la config `tickets.presupuesto.(items|tareas).obligatorios` puede pedir cualquiera de los dos.
// Por eso, si el campo obligatorio es 'costo' o 'precio', se resuelve el alias tomando la clave que tenga
// valor (misma lógica que valorCampoObligatorio en TicketPresupuestoDetallado). Sin esto, pedir 'costo'
// sobre una tarea (que guarda 'precio') bloqueaba "Agregar" aunque el precio estuviera cargado.
//
// Para el monto, 0 es un valor VÁLIDO; solo undefined/null/'' cuentan como faltante. El resto de los campos
// (cantidad, servicio, etc.) mantiene la semántica `!valor`, por lo que 0/''/null/undefined sí faltan.

const tieneValor = (valor) => valor !== undefined && valor !== null && valor !== '';

// VEC-3779: resolver de alias, subido desde TicketPresupuestoDetallado.js para poder reusarlo también
// en el cierre (TicketTareasItemsCierre). Un mismo dato viaja bajo nombres distintos según de dónde
// venga la fila: la clasificación es 'id_clasificacion' cuando la carga el usuario y
// 'id_item_clasificacion' cuando viene del endpoint de presupuestos (que hace `presupuesto_items.*`,
// y la columna se llama así). Validar solo una de las dos claves da falsos faltantes.
export const valorCampoObligatorio = (item, campo) => {
	switch (campo) {
		case 'servicio':      return item.servicio || item.servicio_id || item.servicio_nombre;
		case 'marca':         return item.marca || item.marca_id;
		case 'clasificacion': return item.clasificacion || item.id_clasificacion || item.id_item_clasificacion;
		case 'mecanico':      return (item.mecanico_ids && item.mecanico_ids.length) ? item.mecanico_ids : item.mecanico;
		// Los repuestos usan 'costo'; las tareas usan 'precio'. Soporta config que pida 'precio' o 'costo'.
		case 'precio':
		case 'costo':         return tieneValor(item.costo) ? item.costo : item.precio;
		default:              return item[campo];
	}
};

// VEC-3436: 0 (cero) es un valor válido SOLO para costo/precio; para el resto de los campos el 0 sigue
// siendo faltante.
// VEC-3779: 'cantidad' se compara como NÚMERO. Puede llegar como string desde el TextField del modal de
// edición, y `!"0"` es false: sin el parseFloat, una cantidad en "0" (string) pasaba la validación.
// Los decimales son válidos (VEC-3556/3570 habilitaron 2 decimales en todo el detallado), así que la
// comparación es contra 0, no un chequeo de entero.
export const esValorObligatorioFaltante = (campo, valor) => {
	if (campo === 'costo' || campo === 'precio') {
		return !tieneValor(valor);
	}
	if (campo === 'cantidad') {
		return !tieneValor(valor) || isNaN(parseFloat(valor)) || parseFloat(valor) === 0;
	}
	return !valor;
};

export const faltaCampoObligatorio = (obj, obligatorios) => {
	const lista = obligatorios || [];
	for (let i = 0; i < lista.length; i++) {
		const campo = lista[i];
		if (campo === 'costo' || campo === 'precio') {
			const monto = tieneValor(obj.costo) ? obj.costo : obj.precio;
			if (!tieneValor(monto)) {
				return true;
			}
		} else if (!obj[campo]) {
			return true;
		}
	}
	return false;
};

// VEC-3779: variante usada por el CIERRE (TicketTareasItemsCierre). Se separa de faltaCampoObligatorio
// a propósito, para no cambiar el gate del botón "Agregar" del presupuesto, que es otro flujo.
// Diferencias: resuelve alias (valorCampoObligatorio) y compara la cantidad como número.
// Devuelve la lista de campos faltantes (sin duplicados), no un booleano, para poder decirle al usuario
// qué le falta.
export const camposObligatoriosFaltantes = (filas, obligatorios) => {
	const faltantes = [];
	(filas || []).forEach((fila) => {
		(obligatorios || []).forEach((campo) => {
			if (esValorObligatorioFaltante(campo, valorCampoObligatorio(fila, campo)) && faltantes.indexOf(campo) === -1) {
				faltantes.push(campo);
			}
		});
	});
	return faltantes;
};

// VEC-3779: campos que el cierre puede exigir. `obligatorios` gobierna la CARGA del presupuesto, donde
// descripcion/nombre/precio son editables; en el cierre no lo son, así que exigirlos daría un error del
// que el usuario no puede salir. Con la config real de un tenant
// (items.obligatorios = descripcion,cantidad,clasificacion y tareas.obligatorios = nombre,precio),
// sin esta whitelist todo cierre quedaría bloqueado.
export const CAMPOS_VALIDABLES_CIERRE_ITEMS  = ['cantidad', 'clasificacion'];
// La clasificación NO aplica a tareas: solo existe en el mundo de los ítems (caso trabajaConManoDeObra,
// donde la mano de obra sale de `items`). Hay entornos que trabajan con tareas y se romperían.
export const CAMPOS_VALIDABLES_CIERRE_TAREAS = ['cantidad'];

// Cruce de las tres listas: obligatorio ∧ visible en el cierre ∧ validable. Si el campo no se muestra
// en el cierre no hay forma de cargarlo, así que exigirlo trabaría al usuario.
export const obligatoriosCierre = (obligatorios, showCierre, validables) => {
	return (obligatorios || [])
		.map((c) => c.trim())
		.filter((c) => (showCierre || []).indexOf(c) !== -1 && validables.indexOf(c) !== -1);
};
